import { useState } from "react";
import { Copy, Check, MessageCircle, Mail, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { buildShare } from "@/data/apartmentShare";

const trackShare = (apartmentId, channel) => {
    // Fire-and-forget – never block UI on tracking
    api.post("/shares", { apartment_id: apartmentId, channel }).catch(() => {
        /* silent */
    });
};

/**
 * 3 share actions per apartment: copy link, WhatsApp, E-Mail.
 * Used both publicly (Apartment detail dialog) and in the admin panel.
 *
 * Variants:
 *  - "default" – horizontal pill buttons with text labels
 *  - "compact" – icon-only square buttons (for tight spots)
 */
export const ShareButtons = ({
    apartmentId,
    label = "Diese Wohnung teilen",
    variant = "default",
    className = "",
}) => {
    const [copied, setCopied] = useState(false);
    const share = buildShare(apartmentId);

    if (!share) return null;

    const handleCopy = async () => {
        try {
            // navigator.clipboard requires HTTPS – the preview & production are HTTPS
            await navigator.clipboard.writeText(share.url);
            setCopied(true);
            toast.success("Link kopiert", {
                description: share.url,
                duration: 3000,
            });
            trackShare(apartmentId, "copy");
            setTimeout(() => setCopied(false), 2200);
        } catch (e) {
            // Fallback: show the URL in a prompt-style toast so user can copy manually
            toast.error("Kopieren nicht möglich – Link manuell kopieren:", {
                description: share.url,
                duration: 8000,
            });
        }
    };

    const waUrl = `https://wa.me/?text=${encodeURIComponent(share.whatsappText)}`;
    const mailUrl = `mailto:?subject=${encodeURIComponent(share.subject)}&body=${encodeURIComponent(share.mailBody)}`;

    const isCompact = variant === "compact";
    const baseBtn = isCompact
        ? "inline-flex items-center justify-center w-9 h-9 border transition-colors"
        : "inline-flex items-center gap-2 px-3 py-2 border text-xs font-body font-medium tracking-wide transition-colors";
    const btnIdle = "border-stone-200 text-stone-600 hover:border-brand-500 hover:text-brand-600 bg-white";
    const btnCopied = "border-brand-500 text-brand-600 bg-clay-50";

    return (
        <div className={`${className}`} data-testid={`share-buttons-${apartmentId}`}>
            {!isCompact && label && (
                <div className="flex items-center gap-2 mb-2">
                    <Share2 size={13} className="text-stone-400" />
                    <span className="overline text-stone-400">{label}</span>
                </div>
            )}
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={handleCopy}
                    className={`${baseBtn} ${copied ? btnCopied : btnIdle}`}
                    aria-label="Link kopieren"
                    title="Link kopieren"
                    data-testid={`share-copy-${apartmentId}`}
                >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {!isCompact && (
                        <span>{copied ? "Kopiert" : "Link kopieren"}</span>
                    )}
                </button>

                <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackShare(apartmentId, "whatsapp")}
                    className={`${baseBtn} ${btnIdle}`}
                    aria-label="Per WhatsApp teilen"
                    title="Per WhatsApp teilen"
                    data-testid={`share-whatsapp-${apartmentId}`}
                >
                    <MessageCircle size={14} />
                    {!isCompact && <span>WhatsApp</span>}
                </a>

                <a
                    href={mailUrl}
                    onClick={() => trackShare(apartmentId, "email")}
                    className={`${baseBtn} ${btnIdle}`}
                    aria-label="Per E-Mail teilen"
                    title="Per E-Mail teilen"
                    data-testid={`share-email-${apartmentId}`}
                >
                    <Mail size={14} />
                    {!isCompact && <span>E-Mail</span>}
                </a>
            </div>
        </div>
    );
};
